import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import {
  PermissionMetadataItem,
  PermissionMetadataResponse,
} from "../../../api";
import { ChoosingModal } from "../../../components/ui-kit/choosing-modal/choosing-modal";
import {
  type Column,
  DataTable,
} from "../../../components/ui-kit/tables/data-table";
import { PermissionsModel } from "./permissions-model";
import { isDerivedState, loadPermissionMetadata } from "./permissions-utils";

/**
 * Modal for selecting permissions with automatic handling of implied permissions.
 *
 * Selection uses DataTable's built-in selection mode; {@link PermissionsModel}
 * overrides the selection methods so checking a permission pulls in its implied
 * bits (shown greyed out with an "(implied)" label and a disabled checkbox) and
 * drops any forbidden conflicts (labelled "(excluded)").
 *
 * Uses permission metadata from: /api/v1/policies/permissions-metadata
 */

export const PermissionsModal = observer(function PermissionsModal(props: {
  onConfirm: (params: { selected: any[] }) => void;
  onClose: () => void;
  title: string;
  preselected?: number[];
  loadMetadata?: () => Promise<PermissionMetadataResponse>;
}) {
  const { t } = useLingui();
  const [model, setModel] = useState<PermissionsModel | null>(null);
  const [loading, setLoading] = useState(true);

  const { loadMetadata } = props;
  useEffect(() => {
    const fetchData = async () => {
      const { groups, rules } = await loadPermissionMetadata(loadMetadata);

      // This modal is one flat table with a Category column, not grouped rows.
      const permissionsModel = new PermissionsModel(
        groups.flatMap((group) => group.items),
        rules,
      );
      permissionsModel.init();

      setModel(permissionsModel);
      setLoading(false);
    };

    void fetchData();
  }, [loadMetadata]);

  const columns = useMemo((): Column<PermissionMetadataItem>[] => {
    if (!model) return [];

    return [
      {
        header: <Trans>Category</Trans>,
        align: "left",
        cell: ({ item }) => item.category,
      },
      {
        header: <Trans>Permission</Trans>,
        align: "left",
        width: 300,
        cell: ({ item }) => <PermissionCell model={model} item={item} />,
      },
      {
        header: <Trans>Bit</Trans>,
        width: 30,
        cell: ({ item }) => <BitCell model={model} item={item} />,
      },
    ];
  }, [model]);

  return (
    <ChoosingModal
      model={model ?? undefined}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
      title={props.title}
      fillHeight
      table={
        model && (
          <DataTable
            columns={columns}
            model={model}
            itemKeyField="bit"
            canSelect="multi"
            preselected={props.preselected}
            selectDisabled={model.isSelectDisabled}
            fillHeight
            filterNoun={(count) =>
              t`${plural(count, { one: "permission", other: "permissions" })}`
            }
          />
        )
      }
      loading={loading}
    />
  );
});

const PermissionCell = observer(function PermissionCell(props: {
  model: PermissionsModel;
  item: PermissionMetadataItem;
}) {
  const state = props.model.stateOf(props.item.bit);
  const dimmed = isDerivedState(state);

  // Dim via opacity rather than a fixed muted colour so the text stays legible
  // against both the default background and the pink selected-row background
  // (implied rows are always selected). The colour itself is inherited so it
  // adapts to the row/theme.
  return (
    <span className={dimmed ? "font-normal opacity-70" : undefined}>
      {props.item.description}
      {state === "implied" && (
        <span className="text-2xs ml-2">
          <Trans>(implied)</Trans>
        </span>
      )}
      {state === "forbidden" && (
        <span className="text-2xs text-danger-foreground ml-2">
          <Trans>(excluded)</Trans>
        </span>
      )}
    </span>
  );
});

const BitCell = observer(function BitCell(props: {
  model: PermissionsModel;
  item: PermissionMetadataItem;
}) {
  const dimmed = isDerivedState(props.model.stateOf(props.item.bit));

  return (
    <span className={dimmed ? "opacity-70" : undefined}>{props.item.bit}</span>
  );
});
